(() => {
  const FREE_TAG = "_gwp_free";
  const SESSION_PREFIX = "ocushield_gwp_";
  const PROP_GWP = "_gwp";
  const PROP_HIDE_QTY = "__hide_quantity";

  let syncing = false;
  let suppressCartEvents = false;
  let lastCartJson = null;
  let lockTimer = null;

  function config() {
    return window.gwpConfig || { promos: [], freeTag: FREE_TAG };
  }

  function promos() {
    const list = config().promos;
    return Array.isArray(list) ? list : [];
  }

  function freeTag() {
    return config().freeTag || FREE_TAG;
  }

  function sessionKey(handle) {
    return SESSION_PREFIX + String(handle || "");
  }

  function parseActivationLink(raw) {
    const text = String(raw || "").trim();
    if (!text) return null;

    if (text.includes("://") || text.startsWith("?") || text.indexOf("=") > 0) {
      try {
        const maybeUrl = text.includes("://")
          ? new URL(text)
          : new URL(text.startsWith("?") ? text : "?" + text, window.location.origin);
        const params = {};
        maybeUrl.searchParams.forEach((value, key) => {
          if (key) params[key.toLowerCase()] = value;
        });
        if (Object.keys(params).length) return { type: "params", params };
      } catch (e) {}
    }

    const eq = text.indexOf("=");
    if (eq > 0) {
      return {
        type: "params",
        params: {
          [text.slice(0, eq).trim().toLowerCase()]: text.slice(eq + 1).trim(),
        },
      };
    }

    return { type: "value", value: text };
  }

  function urlMatchesActivation(activationLink) {
    const parsed = parseActivationLink(activationLink);
    if (!parsed) return true;

    const current = new URL(window.location.href);
    if (parsed.type === "params") {
      return Object.keys(parsed.params).every((key) => {
        const expected = parsed.params[key];
        const actual = current.searchParams.get(key);
        return actual != null && String(actual) === String(expected);
      });
    }

    const abralink = current.searchParams.get("abralink");
    if (abralink != null && String(abralink) === parsed.value) return true;
    let matched = false;
    current.searchParams.forEach((value) => {
      if (String(value) === parsed.value) matched = true;
    });
    return matched;
  }

  function captureLinkActivations() {
    promos().forEach((promo) => {
      if (!promo || !promo.handle) return;
      const link = promo.activationLink;
      if (!link || !String(link).trim()) return;
      if (urlMatchesActivation(link)) {
        try {
          sessionStorage.setItem(sessionKey(promo.handle), "1");
        } catch (e) {}
      }
    });
  }

  function isLinkEligible(promo) {
    const link = promo && promo.activationLink;
    if (!link || !String(link).trim()) return true;
    try {
      return sessionStorage.getItem(sessionKey(promo.handle)) === "1";
    } catch (e) {
      return urlMatchesActivation(link);
    }
  }

  function itemProps(item) {
    return (item && item.properties) || {};
  }

  function sameId(a, b) {
    return a != null && b != null && String(a) === String(b);
  }

  function isGiftVariant(item, promo) {
    if (!item || !promo) return false;
    if (promo.variantId != null && sameId(item.variant_id, promo.variantId)) return true;
    if (promo.productId != null && sameId(item.product_id, promo.productId)) return true;
    return false;
  }

  function isMarkedGwp(item) {
    const props = itemProps(item);
    return Boolean(props[PROP_GWP]);
  }

  function isGwpLine(item, promo) {
    if (!item || !promo) return false;
    // Prefer explicit GWP marker; also treat configured gift variant as the gift line
    if (isMarkedGwp(item) && isGiftVariant(item, promo)) return true;
    if (isGiftVariant(item, promo)) return true;
    return false;
  }

  function gwpProductIds(allPromos) {
    return (allPromos || promos())
      .map((p) => (p && p.productId != null ? String(p.productId) : null))
      .filter(Boolean);
  }

  function gwpVariantIds(allPromos) {
    return (allPromos || promos())
      .map((p) => (p && p.variantId != null ? String(p.variantId) : null))
      .filter(Boolean);
  }

  function isConfiguredGiftProduct(item, allPromos) {
    if (!item) return false;
    const pids = gwpProductIds(allPromos);
    const vids = gwpVariantIds(allPromos);
    return (
      pids.indexOf(String(item.product_id)) !== -1 ||
      vids.indexOf(String(item.variant_id)) !== -1
    );
  }

  function isFreeOrGwpLine(item, allPromos) {
    if (!item) return false;
    if (isMarkedGwp(item)) return true;
    if (isConfiguredGiftProduct(item, allPromos)) return true;
    return false;
  }

  function isPaidQualifyingLine(item, promo) {
    if (!item) return false;
    const all = promos();
    if (isFreeOrGwpLine(item, all)) return false;

    const ids = promo && Array.isArray(promo.eligibleProductIds) ? promo.eligibleProductIds : [];
    if (promo && promo.collectionId && ids.length) {
      return ids.map(String).indexOf(String(item.product_id)) !== -1;
    }
    return true;
  }

  function fetchCart() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }

  function cartAdd(variantId, properties) {
    return fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: properties || {},
      }),
    }).then((r) => r.json());
  }

  function cartChange(key, quantity) {
    if (!key) return Promise.resolve(null);
    return fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then((r) => r.json());
  }

  function beginSuppress() {
    suppressCartEvents = true;
  }

  function endSuppress(ms) {
    clearTimeout(endSuppress._t);
    endSuppress._t = setTimeout(function () {
      suppressCartEvents = false;
    }, ms || 800);
  }

  function syncRebuyQuiet() {
    try {
      beginSuppress();
      if (window.Rebuy && Rebuy.Cart && typeof Rebuy.Cart.fetchShopifyCart === "function") {
        Rebuy.Cart.fetchShopifyCart(function () {
          lockRebuyGwpLines();
          updateCheckoutButtons(lastCartJson);
          endSuppress(800);
        });
      } else {
        lockRebuyGwpLines();
        endSuppress(300);
      }
    } catch (e) {
      lockRebuyGwpLines();
      endSuppress(300);
    }
  }

  function lockRebuyGwpLines() {
    const all = promos();
    const pids = gwpProductIds(all);
    const vids = gwpVariantIds(all);
    if (!pids.length && !vids.length) return;

    const cart = (window.Rebuy && Rebuy.Cart && Rebuy.Cart.cart) || lastCartJson;
    const items = cart && Array.isArray(cart.items) ? cart.items : [];

    // Attribute lock on known gift lines from cart data
    items.forEach((item) => {
      if (!isFreeOrGwpLine(item, all)) return;
      const matches = document.querySelectorAll(
        [
          '.rebuy-cart__flyout-item[data-key="' + item.key + '"]',
          '.rebuy-cart__flyout-item[data-variant-id="' + item.variant_id + '"]',
          '.rebuy-cart__flyout-item[data-product-id="' + item.product_id + '"]',
        ].join(",")
      );
      matches.forEach(stampLockedItem);
    });

    // Also lock by configured ids in case Rebuy DOM attributes differ from cart keys
    pids.forEach((pid) => {
      document
        .querySelectorAll('.rebuy-cart__flyout-item[data-product-id="' + pid + '"]')
        .forEach(stampLockedItem);
    });
    vids.forEach((vid) => {
      document
        .querySelectorAll('.rebuy-cart__flyout-item[data-variant-id="' + vid + '"]')
        .forEach(stampLockedItem);
    });
  }

  function stampLockedItem(el) {
    if (!el) return;
    el.setAttribute("data-gwp-hide-qty", "true");
    el.setAttribute("data-abra-hide-qty", "true");
    el.classList.add("gwp-line-locked");
    el.querySelectorAll(
      [
        ".rebuy-cart__flyout-item-quantity",
        ".rebuy-cart__flyout-item-quantity-widget",
        ".rebuy-cart__flyout-item-quantity-widget-button",
        ".rebuy-cart__flyout-item-quantity-widget-label",
        ".rebuy-cart__flyout-item-quantity-trigger",
        "input[type='number']",
        "input.js-qty__num",
      ].join(",")
    ).forEach((node) => {
      node.hidden = true;
      node.setAttribute("disabled", "disabled");
      node.setAttribute("aria-hidden", "true");
      if (node.style) {
        node.style.display = "none";
        node.style.visibility = "hidden";
        node.style.pointerEvents = "none";
      }
    });
  }

  function scheduleLock() {
    clearTimeout(lockTimer);
    lockTimer = setTimeout(lockRebuyGwpLines, 50);
  }

  function checkoutSelectors() {
    return [
      'button[name="checkout"]',
      'input[name="checkout"]',
      "#checkout",
      ".cart__checkout",
      ".cart__checkout-wrapper button",
      ".rebuy-cart__checkout-button",
      'a[href="/checkout"]',
    ].join(",");
  }

  function setCheckoutDisabled(disabled) {
    document.querySelectorAll(checkoutSelectors()).forEach((el) => {
      if (disabled) {
        el.setAttribute("disabled", "disabled");
        el.setAttribute("aria-disabled", "true");
        el.classList.add("gwp-checkout-disabled");
        if (el.tagName === "A") {
          el.setAttribute("data-gwp-href", el.getAttribute("href") || "");
          el.removeAttribute("href");
        }
      } else if (el.classList.contains("gwp-checkout-disabled")) {
        el.removeAttribute("disabled");
        el.removeAttribute("aria-disabled");
        el.classList.remove("gwp-checkout-disabled");
        if (el.tagName === "A" && el.hasAttribute("data-gwp-href")) {
          el.setAttribute("href", el.getAttribute("data-gwp-href"));
          el.removeAttribute("data-gwp-href");
        }
      }
    });
  }

  function cartIsGiftOnly(cart, allPromos) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return false;
    return cart.items.every((item) => isFreeOrGwpLine(item, allPromos));
  }

  function updateCheckoutButtons(cart) {
    setCheckoutDisabled(cartIsGiftOnly(cart, promos()));
  }

  function findGiftLine(cart, promo) {
    if (!cart || !Array.isArray(cart.items) || !promo) return null;
    // Prefer explicitly marked GWP lines
    const marked = cart.items.find(
      (item) => isMarkedGwp(item) && isGiftVariant(item, promo)
    );
    if (marked) return marked;
    return cart.items.find((item) => isGiftVariant(item, promo)) || null;
  }

  function hasQualifyingLine(cart, promo) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some((item) => isPaidQualifyingLine(item, promo));
  }

  async function reconcilePromo(cart, promo) {
    if (!promo || !promo.variantId) return { cart: cart, changed: false };
    let changed = false;

    if (!isLinkEligible(promo)) {
      const gift = findGiftLine(cart, promo);
      if (gift && gift.key) {
        cart = (await cartChange(gift.key, 0)) || cart;
        changed = true;
      }
      return { cart: cart, changed: changed };
    }

    const qualifies = hasQualifyingLine(cart, promo);
    const gift = findGiftLine(cart, promo);

    if (!qualifies) {
      if (gift && gift.key) {
        cart = (await cartChange(gift.key, 0)) || cart;
        changed = true;
      }
      return { cart: cart, changed: changed };
    }

    if (!gift) {
      const addResult = await cartAdd(promo.variantId, {
        [PROP_GWP]: "1",
        [PROP_HIDE_QTY]: "1",
      });
      // Failed adds look like { status: 422, message: "..." }
      if (addResult && addResult.status) {
        return { cart: cart, changed: false };
      }
      cart = (await fetchCart()) || cart;
      // Confirm gift is present before treating as changed (prevents add spam)
      if (findGiftLine(cart, promo)) changed = true;
      return { cart: cart, changed: changed };
    }

    if (Number(gift.quantity) > 1 && gift.key) {
      cart = (await cartChange(gift.key, 1)) || cart;
      changed = true;
    }

    return { cart: cart, changed: changed };
  }

  async function reconcileAll() {
    if (syncing || suppressCartEvents) return;
    syncing = true;
    beginSuppress();
    let changed = false;
    try {
      let cart = await fetchCart();
      if (!cart) return;

      const all = promos();
      if (!all.length) {
        lastCartJson = cart;
        updateCheckoutButtons(cart);
        return;
      }

      if (cartIsGiftOnly(cart, all)) {
        setCheckoutDisabled(true);
        for (const item of cart.items.slice()) {
          if (isFreeOrGwpLine(item, all) && item.key) {
            cart = (await cartChange(item.key, 0)) || cart;
            changed = true;
          }
        }
        cart = (await fetchCart()) || cart;
        lastCartJson = cart;
        updateCheckoutButtons(cart);
        if (changed) syncRebuyQuiet();
        else {
          lockRebuyGwpLines();
          endSuppress(300);
        }
        return;
      }

      for (const promo of all) {
        const result = await reconcilePromo(cart, promo);
        cart = result.cart || cart;
        if (result.changed) changed = true;
      }

      lastCartJson = cart;
      updateCheckoutButtons(cart);
      lockRebuyGwpLines();
      if (changed) syncRebuyQuiet();
      else endSuppress(300);
    } catch (e) {
      endSuppress(300);
    } finally {
      syncing = false;
    }
  }

  function scheduleReconcile() {
    if (suppressCartEvents || syncing) return;
    clearTimeout(scheduleReconcile._t);
    scheduleReconcile._t = setTimeout(reconcileAll, 250);
  }

  function productPageIsFreeGift() {
    const root = document.querySelector("[data-section-type='product']");
    if (!root) return false;
    const tags = (root.getAttribute("data-product-tags") || "")
      .split(",")
      .map((t) => t.trim());
    if (tags.indexOf(freeTag()) !== -1) return true;
    const handle = root.getAttribute("data-product-handle");
    return promos().some((p) => p && p.productHandle && p.productHandle === handle);
  }

  function cartHasAnyQualifying(cart) {
    return promos().filter(isLinkEligible).some((promo) => hasQualifyingLine(cart, promo));
  }

  function bindAloneAtcGuard() {
    document.addEventListener(
      "submit",
      function (evt) {
        const form = evt.target;
        if (!form || !form.getAttribute) return;
        if (form.getAttribute("data-gwp-bypass") === "1") {
          form.removeAttribute("data-gwp-bypass");
          return;
        }
        const action = form.getAttribute("action") || "";
        if (action.indexOf("/cart/add") === -1) return;
        if (!productPageIsFreeGift()) return;

        evt.preventDefault();
        evt.stopPropagation();

        fetchCart().then((cart) => {
          lastCartJson = cart;
          if (!cartHasAnyQualifying(cart)) {
            window.alert("This free gift can only be added with another product.");
            return;
          }
          form.setAttribute("data-gwp-bypass", "1");
          if (typeof form.requestSubmit === "function") form.requestSubmit();
          else form.submit();
        });
      },
      true
    );
  }

  function bindCheckoutGuard() {
    document.addEventListener(
      "click",
      function (evt) {
        const el = evt.target && evt.target.closest ? evt.target.closest(checkoutSelectors()) : null;
        if (!el) return;
        if (!cartIsGiftOnly(lastCartJson, promos())) return;
        evt.preventDefault();
        evt.stopPropagation();
        setCheckoutDisabled(true);
        scheduleReconcile();
      },
      true
    );

    // Block qty edits on locked GWP lines in Rebuy
    document.addEventListener(
      "click",
      function (evt) {
        const locked = evt.target && evt.target.closest
          ? evt.target.closest(".rebuy-cart__flyout-item[data-gwp-hide-qty], .rebuy-cart__flyout-item.gwp-line-locked")
          : null;
        if (!locked) return;
        if (
          evt.target.closest(
            ".rebuy-cart__flyout-item-quantity, .rebuy-cart__flyout-item-quantity-widget, button, input"
          )
        ) {
          // Allow remove button
          if (evt.target.closest(".rebuy-cart__flyout-item-remove, [data-remove]")) return;
          const isQty =
            evt.target.closest(
              ".rebuy-cart__flyout-item-quantity, .rebuy-cart__flyout-item-quantity-widget"
            ) ||
            (evt.target.tagName === "BUTTON" &&
              /plus|minus|quantity|qty/i.test(
                (evt.target.className || "") + " " + (evt.target.getAttribute("aria-label") || "")
              ));
          if (isQty) {
            evt.preventDefault();
            evt.stopPropagation();
          }
        }
      },
      true
    );
  }

  function bindCartListeners() {
    document.addEventListener("rebuy:cart.change", scheduleReconcile);
    document.addEventListener("rebuy:smartcart.ready", function () {
      scheduleReconcile();
      scheduleLock();
    });
    document.addEventListener("cart:updated", scheduleReconcile);
    document.addEventListener("cart:build", scheduleLock);

    const obs = new MutationObserver(function () {
      scheduleLock();
    });
    if (document.body) {
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  function qtyHideCssForAttr(attr, id) {
    const root = '.rebuy-cart__flyout-item[' + attr + '="' + id + '"]';
    return (
      root +
      " .rebuy-cart__flyout-item-quantity," +
      root +
      " .rebuy-cart__flyout-item-quantity-widget," +
      root +
      " .rebuy-cart__flyout-item-quantity-widget-button," +
      root +
      " .rebuy-cart__flyout-item-quantity-widget-label" +
      "{display:none!important;visibility:hidden!important;pointer-events:none!important;width:0!important;height:0!important;overflow:hidden!important;margin:0!important;padding:0!important;border:0!important;}"
    );
  }

  function injectGiftLockStyles() {
    const vids = gwpVariantIds(promos());
    const pids = gwpProductIds(promos());
    if (!vids.length && !pids.length) return;
    let css = "";
    vids.forEach((vid) => {
      css += qtyHideCssForAttr("data-variant-id", vid);
    });
    pids.forEach((pid) => {
      css += qtyHideCssForAttr("data-product-id", pid);
    });
    // Also hide whenever our lock attributes are present
    css +=
      ".rebuy-cart__flyout-item[data-gwp-hide-qty] .rebuy-cart__flyout-item-quantity," +
      ".rebuy-cart__flyout-item[data-gwp-hide-qty] .rebuy-cart__flyout-item-quantity-widget," +
      ".rebuy-cart__flyout-item[data-gwp-hide-qty] .rebuy-cart__flyout-item-quantity-widget-button," +
      ".rebuy-cart__flyout-item.gwp-line-locked .rebuy-cart__flyout-item-quantity," +
      ".rebuy-cart__flyout-item.gwp-line-locked .rebuy-cart__flyout-item-quantity-widget," +
      ".rebuy-cart__flyout-item.gwp-line-locked .rebuy-cart__flyout-item-quantity-widget-button" +
      "{display:none!important;visibility:hidden!important;pointer-events:none!important;}";

    let style = document.getElementById("gwp-gift-lock-styles");
    if (!style) {
      style = document.createElement("style");
      style.id = "gwp-gift-lock-styles";
      document.head.appendChild(style);
    }
    style.textContent = css;
  }

  // Rebuy natively skips rendering qty when hideQuantitySelectors(item) is true
  // (also honors product tag smart-cart-hide-qty).
  function patchRebuyHideQuantitySelectors() {
    const smartCart = window.Rebuy && Rebuy.SmartCart;
    if (!smartCart || smartCart.__gwpHideQtyPatched) return;

    const original = smartCart.hideQuantitySelectors
      ? smartCart.hideQuantitySelectors.bind(smartCart)
      : null;

    smartCart.hideQuantitySelectors = function (item) {
      if (original && original(item)) return true;
      if (!item) return false;
      const props = item.properties || {};
      if (props[PROP_GWP] || props[PROP_HIDE_QTY]) return true;
      if (isConfiguredGiftProduct(item, promos())) return true;
      const tags = (item.product_tags || item.tags || []).map(String);
      if (tags.indexOf("smart-cart-hide-qty") !== -1) return true;
      if (tags.indexOf(freeTag()) !== -1) return true;
      return false;
    };
    smartCart.__gwpHideQtyPatched = true;

    // Force Rebuy to re-render line items if already ready
    try {
      if (typeof smartCart.render === "function") smartCart.render();
      else if (window.Rebuy && Rebuy.Cart && typeof Rebuy.Cart.fetchShopifyCart === "function") {
        Rebuy.Cart.fetchShopifyCart(function () {
          scheduleLock();
        });
      }
    } catch (e) {}
  }

  function bindRebuyHideQtyPatch() {
    patchRebuyHideQuantitySelectors();
    document.addEventListener("rebuy:smartcart.ready", patchRebuyHideQuantitySelectors);
    document.addEventListener("rebuy:cart.change", patchRebuyHideQuantitySelectors);
    // Rebuy may boot after us
    let attempts = 0;
    const timer = setInterval(function () {
      attempts += 1;
      patchRebuyHideQuantitySelectors();
      if ((window.Rebuy && Rebuy.SmartCart && Rebuy.SmartCart.__gwpHideQtyPatched) || attempts > 40) {
        clearInterval(timer);
      }
    }, 250);
  }

  function init() {
    if (!promos().length) return;
    captureLinkActivations();
    injectGiftLockStyles();
    bindRebuyHideQtyPatch();
    bindCartListeners();
    bindAloneAtcGuard();
    bindCheckoutGuard();
    scheduleReconcile();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
